import Errors from './Errors';
import DateManager from './DateManager';

class Form {
  /**
   *  Store the data in an original data property
   *  and create properties for the data input
   *
   * @param {object} data
   * @param {Boolean} autoclear
   */
  constructor(data, autoclear = false) {
    if (Object.keys(data).length) {
      this.originalData = data;
    } else {
      this.originalData = {};
    }

    this.files = {};

    for (let field in data) {
      this[field] = data[field];
    }

    this.autoclear = autoclear;
    this.isProcessing = false;
    this.errors = new Errors();
    this.dateManager = new DateManager();
  }

  /**
   * Add data to the current form instance
   *
   * @param {string} key The key of the data item
   * @param {string} value The value of the data item
   */
  addData(key, value) {
    this.originalData[key] = value;
    this[key] = value;
  }

  /**
   * Check to see if the form has the current input field
   *
   * @param {string} key The key to check for
   * @return boolean
   */
  hasData(key) {
    return !!this[key];
  }

  /**
   * Retrieve the data for the form
   *
   * @returns {object}
   */
  data() {
    let data = {};

    for (let property in this.originalData) {
      data[property] = this[property];
    }

    return data;
  }

  /**
   *
   * Get the data for the form
   *
   * @param key
   * @returns {*}
   */
  getData(key) {
    if (this.hasData(key)) {
      return this[key];
    }

    return null;
  }

  /**
   * Retrieve the data for the form as a FormData object
   *
   * @returns {FormData}
   */
  formData() {
    let formData = new FormData();

    for (var property in this.originalData) {
      if (
        Object.prototype.toString.call(this[property]) === '[object Object]'
      ) {
        for (let key in this[property]) {
          // Check if the nested property is also an object, if it is then we
          // want to convert the whole object to JSON
          if (
            Object.prototype.toString.call(this[property][key]) ===
            '[object Object]'
          ) {
            formData.append(property, JSON.stringify(this[property]));
            break;
          }

          if (this[property][key] !== null && this[property][key] !== 'null') {
            formData.append(property + '[' + key + ']', this[property][key]);
          }
        }
      } else if (
        Object.prototype.toString.call(this[property]) === '[object Date]'
      ) {
        formData.append(
          property,
          this.dateManager.format(this[property], 'YYYY-MM-DD HH:mm:ss')
        );
      } else if (Array.isArray(this[property])) {
        formData.append(property, this.jsonEncode(this[property]));
      } else if (this[property] !== null && this[property] !== 'null') {
        formData.append(property, this[property]);
      }
    }

    for (let property in this.files) {
      formData.append(property, this.files[property]);
    }

    return formData;
  }

  jsonEncode(value) {
    return JSON.stringify(this.formatForJson(value));
  }

  formatForJson(value) {
    if (Array.isArray(value)) {
      value.map(innerValue => this.formatForJson(innerValue));
    } else if (Object.prototype.toString.call(value === '[object Object]')) {
      for (let key in value) {
        if (Object.prototype.toString.call(value[key]) === '[object Object]') {
          value[key] = this.formatForJson(value[key]);
        }

        if (Object.prototype.toString.call(value[key]) === '[object Date]') {
          value[key] = this.dateManager.format(
            value[key],
            'YYYY-MM-DD HH:mm:ss'
          );
        }
      }
    } else if (Object.prototype.toString.call(value) === '[object Date]') {
      return this.dateManager.format(value, 'YYYY-MM-DD HH:mm:ss');
    }

    return value;
  }

  /**
   * Add a file to the file list
   *
   * @param {string} name
   * @param {File} file
   */
  addFile(name, file) {
    this.files[name] = file;
  }

  /**
   * Remove a file to the file list
   *
   * @param {string} name
   */
  removeFile(name) {
    delete this.files[name];
  }

  /**
   * Remove an item from the data
   *
   * @param {string} name
   */
  removeItem(key) {
    if (this.hasData(key)) {
      delete this[key];
      delete this.originalData[key];
    }
  }

  /**
   * Check to see if the given field
   *
   * @param {string} field
   * @returns {Boolean}
   */
  isEmpty(field) {
    if (Object.prototype.toString.call(this[field]) === '[object Object]') {
      return !Object.keys(this[field]).length > 0;
    } else {
      return (
        this[field] === undefined || this[field] === null || this[field] === ''
      );
    }
  }

  /**
   * Reset the form
   */
  reset() {
    for (let field in this.originalData) {
      this[field] = null;
    }

    this.errors.clear();
  }

  /**
   * Remove all keys and values
   */
  wipe() {
    for (let field in this.originalData) {
      delete this[field];
    }

    this.originalData = {};
  }

  /**
   * Submit a post request
   *
   * @param {string} url
   * @returns {Promise}
   */
  post(url) {
    return this.submit('post', url);
  }

  /**
   * Submit a patch request
   *
   * @param {string} url
   * @returns {Promise}
   */
  patch(url) {
    return this.submit('patch', url);
  }

  /**
   * Submit a put request
   *
   * @param {string} url
   * @returns {Promise}
   */
  put(url) {
    return this.submit('put', url);
  }

  /**
   * Submit the form
   *
   * @param {string} requestType
   * @param {string} url
   * @returns {Promise}
   */
  submit(requestType, url) {
    return new Promise((resolve, reject) => {
      this.isProcessing = true;

      let data = this.formData();

      if (requestType == 'put' || requestType == 'patch') {
        url += '?_method=' + requestType.toUpperCase();
        requestType = 'post';
      }

      window.Ajax.request(requestType, url, data)
        .then(response => {
          this.onSuccess(response.data);

          resolve(response.data);
        })
        .catch(error => {
          let data = error.response ? error.response.data : error;

          if (data['errors'] && data['message']) {
            this.onFail(data.errors);
            reject(data.errors);
            return;
          }

          this.onFail(data);
          reject(data);
        });
    });
  }

  /**
   * Handle a successful form submission
   *
   * @param {object} data
   */
  onSuccess(data) {
    this.isProcessing = false;

    if (this.autoclear) {
      this.reset();
    }
  }

  /**
   * Handle a failed form submission
   *
   * @param {object} errors
   */
  onFail(errors) {
    this.isProcessing = false;
    this.errors.record(errors);
  }

  /**
   * Return whether or not a request is currently being made
   *
   * @returns {Boolean}
   */
  processing() {
    return this.isProcessing;
  }
}

export default Form;
